import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { AccessDatatable } from '@/react/portainer/access-control/AccessManagement/AccessDatatable/AccessDatatable';
import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';
import { useAccesses } from '@/react/portainer/environments/queries/useAccesses';
import { useUpdateGroupAccessMutation } from '@/react/portainer/environments/environment-groups/mutations/useUpdateGroupAccessMutation';

import { Alert } from '@@/Alert';

interface Props {
  group?: EnvironmentGroup;
}

export function AccessDatatableWrapper({ group }: Props) {
  const { authorizedUsersAndTeams, policyID, policyName, isLoading, error } =
    useAccesses(undefined, group);
  const updateMutation = useUpdateGroupAccessMutation();

  if (!authorizedUsersAndTeams) {
    return null;
  }

  if (error) {
    return (
      <Alert color="error" title="Error loading access">
        {error.message ?? 'Unknown error'}
      </Alert>
    );
  }

  return (
    <AccessDatatable
      tableKey="access_group"
      dataset={authorizedUsersAndTeams}
      onRemove={handleRemove}
      onUpdate={handleUpdate}
      showWarning
      isUpdateEnabled
      showRoles
      inheritFrom={false}
      policyId={policyID}
      policyName={policyName}
      isUpdatingAccess={updateMutation.isLoading}
      isLoading={isLoading}
    />
  );

  async function handleUpdate(
    updatedUserAccesses: Array<UserAccessViewModel>,
    updatedTeamAccesses: Array<TeamAccessViewModel>
  ) {
    if (!group) {
      return;
    }

    try {
      const newUserPolicies = { ...group.UserAccessPolicies };
      const newTeamPolicies = { ...group.TeamAccessPolicies };

      updatedUserAccesses.forEach((access) => {
        newUserPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      updatedTeamAccesses.forEach((access) => {
        newTeamPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      await updateMutation.mutateAsync({
        id: group.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }

  async function handleRemove(
    selectedAccesses: Array<UserAccessViewModel | TeamAccessViewModel>
  ) {
    if (!group) {
      return;
    }

    try {
      const newUserPolicies = { ...group.UserAccessPolicies };
      const newTeamPolicies = { ...group.TeamAccessPolicies };

      selectedAccesses.forEach((access) => {
        if (access.Type === 'user') {
          delete newUserPolicies[access.Id];
        } else {
          delete newTeamPolicies[access.Id];
        }
      });

      await updateMutation.mutateAsync({
        id: group.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully removed');
    } catch (error) {
      notifyError('Failed to remove access', error as Error);
    }
  }
}
